import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus, FaStickyNote, FaTrash } from 'react-icons/fa';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import { useAuth } from '../../../context/AuthContext';
import { ProjectsRestDataProvider, Project } from '../../../data/ProjectsRestDataProvider';
import { TimeWritingRestDataProvider } from '../../../data/TimeWritingRestDataProvider';
import { TimeWritingEntry } from '../../../models/TimeWritingEntry';
import './TimeWriting.scss';
import '../../../styles/GenericSiteStyles.scss';
import { useTranslation } from 'react-i18next';

// ─── ISO week helpers ────────────────────────────────────────────────────────

function getISOWeek(date: Date): { year: number; week: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.valueOf() - yearStart.valueOf()) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}

function getWeekDates(year: number, week: number): Date[] {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (dayOfWeek - 1) + (week - 1) * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    return d;
  });
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function formatDayLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function formatWeekRange(dates: Date[]): string {
  const short: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', timeZone: 'UTC' };
  const long: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
  return `${dates[0].toLocaleDateString('en-GB', short)} – ${dates[6].toLocaleDateString('en-GB', long)}`;
}

function prevWeek(year: number, week: number): { year: number; week: number } {
  if (week > 1) return { year, week: week - 1 };
  return getISOWeek(new Date(Date.UTC(year - 1, 11, 28)));
}

function nextWeek(year: number, week: number): { year: number; week: number } {
  const lastWeek = getISOWeek(new Date(Date.UTC(year, 11, 28))).week;
  if (week < lastWeek) return { year, week: week + 1 };
  return { year: year + 1, week: 1 };
}

function maxWeekOfYear(year: number): number {
  return getISOWeek(new Date(Date.UTC(year, 11, 28))).week;
}

// ─── Row state types ─────────────────────────────────────────────────────────

interface DayCell {
  hours: string;
  description: string;
  entryId: string | null;
}

interface TimeWritingRow {
  rowKey: string;
  projectId: string;
  days: DayCell[];
}

function emptyDays(): DayCell[] {
  return Array.from({ length: 7 }, () => ({ hours: '', description: '', entryId: null }));
}

function newRow(): TimeWritingRow {
  return { rowKey: crypto.randomUUID(), projectId: '', days: emptyDays() };
}

function buildRowsFromEntries(entries: TimeWritingEntry[], weekDates: Date[]): TimeWritingRow[] {
  const byProject = new Map<string, TimeWritingRow>();
  for (const entry of entries) {
    if (!byProject.has(entry.projectId)) {
      byProject.set(entry.projectId, {
        rowKey: crypto.randomUUID(),
        projectId: entry.projectId,
        days: emptyDays(),
      });
    }
    const row = byProject.get(entry.projectId)!;
    const dayIndex = weekDates.findIndex(d => formatDate(d) === entry.date);
    if (dayIndex >= 0) {
      row.days[dayIndex] = {
        hours: entry.hours > 0 ? String(entry.hours) : '',
        description: entry.activityDescription ?? '',
        entryId: entry.id || null,
      };
    }
  }
  const result = Array.from(byProject.values());
  return result.length > 0 ? result : [newRow()];
}

// ─── Component ───────────────────────────────────────────────────────────────

const TimeWriting: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const { user } = useAuth();
  const today = new Date();
  const { year: todayYear, week: todayWeek } = getISOWeek(today);

  const [currentYear, setCurrentYear] = useState(todayYear);
  const [currentWeek, setCurrentWeek] = useState(todayWeek);
  const [jumpYear, setJumpYear] = useState(todayYear);
  const [jumpWeek, setJumpWeek] = useState(todayWeek);

  const [projects, setProjects] = useState<Project[]>([]);
  const [rows, setRows] = useState<TimeWritingRow[]>([newRow()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [noteModal, setNoteModal] = useState<{
    open: boolean;
    rowIndex: number;
    dayIndex: number;
    text: string;
  }>({ open: false, rowIndex: 0, dayIndex: 0, text: '' });

  const projectProvider = useMemo(() => new ProjectsRestDataProvider(), []);
  const timeProvider = useMemo(() => new TimeWritingRestDataProvider(), []);
  const weekDates = useMemo(() => getWeekDates(currentYear, currentWeek), [currentYear, currentWeek]);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const [projectList, entries] = await Promise.all([
        projectProvider.getAll(),
        timeProvider.getByUserAndDateRange(user.id, formatDate(weekDates[0]), formatDate(weekDates[6])),
      ]);
      setProjects(projectList);
      setRows(buildRowsFromEntries(entries, weekDates));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user, projectProvider, timeProvider, weekDates]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Navigation ────────────────────────────────────────────────────────────

  const navigate = (year: number, week: number) => {
    setCurrentYear(year);
    setCurrentWeek(week);
    setJumpYear(year);
    setJumpWeek(week);
  };

  const goToPrevWeek = () => {
    const nw = prevWeek(currentYear, currentWeek);
    navigate(nw.year, nw.week);
  };

  const goToNextWeek = () => {
    const nw = nextWeek(currentYear, currentWeek);
    navigate(nw.year, nw.week);
  };

  const goToToday = () => navigate(todayYear, todayWeek);

  const handleJump = () => {
    const max = maxWeekOfYear(jumpYear);
    navigate(jumpYear, Math.max(1, Math.min(jumpWeek, max)));
  };

  // ─── Row mutations ─────────────────────────────────────────────────────────

  const addRow = () => setRows(prev => [...prev, newRow()]);

  const removeRow = (rowIdx: number) => {
    const row = rows[rowIdx];
    row.days.forEach(cell => {
      if (cell.entryId) timeProvider.delete(cell.entryId).catch(() => {});
    });
    setRows(prev => prev.filter((_, i) => i !== rowIdx));
  };

  const changeRowProject = (rowIdx: number, projectId: string) => {
    setRows(prev => prev.map((row, i) => {
      if (i !== rowIdx) return row;
      if (row.projectId && row.projectId !== projectId) {
        row.days.forEach(cell => {
          if (cell.entryId) timeProvider.delete(cell.entryId).catch(() => {});
        });
        return { ...row, projectId, days: emptyDays() };
      }
      return { ...row, projectId };
    }));
  };

  const changeHours = (rowIdx: number, dayIdx: number, value: string) => {
    setRows(prev => prev.map((row, i) => {
      if (i !== rowIdx) return row;
      const days = [...row.days];
      days[dayIdx] = { ...days[dayIdx], hours: value };
      return { ...row, days };
    }));
  };

  // Save cell on blur; currentHoursValue comes from the input element to avoid stale closure
  const saveCell = async (rowIdx: number, dayIdx: number, currentHoursValue: string) => {
    const row = rows[rowIdx];
    if (!row.projectId || !user) return;
    const cell = row.days[dayIdx];
    const parsedHours = parseFloat(currentHoursValue);
    const date = formatDate(weekDates[dayIdx]);

    if (!isNaN(parsedHours) && parsedHours > 0) {
      const entryId = cell.entryId ?? crypto.randomUUID();
      const entry: TimeWritingEntry = {
        id: entryId,
        userId: user.id,
        date,
        projectId: row.projectId,
        hours: parsedHours,
        activityDescription: cell.description,
      };
      try {
        if (cell.entryId) {
          await timeProvider.update(cell.entryId, entry);
        } else {
          const created = await timeProvider.create(entry);
          setRows(prev => prev.map((r, i) => {
            if (i !== rowIdx) return r;
            const days = [...r.days];
            days[dayIdx] = { ...days[dayIdx], entryId: created.id ?? entryId };
            return { ...r, days };
          }));
        }
      } catch { /* toast already shown by data provider */ }
    } else if (cell.entryId) {
      try {
        await timeProvider.delete(cell.entryId);
        setRows(prev => prev.map((r, i) => {
          if (i !== rowIdx) return r;
          const days = [...r.days];
          days[dayIdx] = { ...days[dayIdx], entryId: null, hours: '' };
          return { ...r, days };
        }));
      } catch { /* toast already shown by data provider */ }
    }
  };

  // ─── Note modal ─────────────────────────────────────────────────────────────

  const openNoteModal = (rowIdx: number, dayIdx: number) => {
    const cell = rows[rowIdx].days[dayIdx];
    setNoteModal({ open: true, rowIndex: rowIdx, dayIndex: dayIdx, text: cell.description });
  };

  const saveNote = async () => {
    const { rowIndex, dayIndex, text } = noteModal;

    // Capture refs before closing
    const row = rows[rowIndex];
    const cell = row.days[dayIndex];

    setRows(prev => prev.map((r, i) => {
      if (i !== rowIndex) return r;
      const days = [...r.days];
      days[dayIndex] = { ...days[dayIndex], description: text };
      return { ...r, days };
    }));
    setNoteModal(prev => ({ ...prev, open: false }));

    // Persist if entry already exists
    if (cell.entryId && user) {
      const parsedHours = parseFloat(cell.hours);
      if (!isNaN(parsedHours) && parsedHours > 0) {
        const entry: TimeWritingEntry = {
          id: cell.entryId,
          userId: user.id,
          date: formatDate(weekDates[dayIndex]),
          projectId: row.projectId,
          hours: parsedHours,
          activityDescription: text,
        };
        await timeProvider.update(cell.entryId, entry).catch(() => {});
      }
    }
  };

  // ─── Derived values ─────────────────────────────────────────────────────────

  const dayNames = useMemo(() => [
    t('common:days.mon'), t('common:days.tue'), t('common:days.wed'),
    t('common:days.thu'), t('common:days.fri'), t('common:days.sat'), t('common:days.sun'),
  ], [t]);

  const weekRange = useMemo(() => formatWeekRange(weekDates), [weekDates]);
  const isCurrentWeek = currentYear === todayYear && currentWeek === todayWeek;
  const activeProjects = useMemo(
    () => projects.filter(p => p.status !== 'Planning' && p.status !== 'Completed'),
    [projects]
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="timewriting-page">
      <MainScreenHeader
        title={t('screens:timeWriting.title')}
        subtitle={t('screens:timeWriting.weekSubtitle', { week: currentWeek, range: weekRange })}
      />

      <div className="timewriting-container">

        {/* Week navigation bar */}
        <div className="timewriting-nav">
          <button className="timewriting-nav-btn" onClick={goToPrevWeek}>
            &#8249;&nbsp;{t('common:prev')}
          </button>

          {!isCurrentWeek && (
            <button className="timewriting-nav-btn" onClick={goToToday}>
              {t('common:today')}
            </button>
          )}

          <div className="timewriting-jump">
            <label className="timewriting-jump-label">{t('common:year')}</label>
            <input
              type="number"
              className="timewriting-jump-input"
              value={jumpYear}
              min={2000}
              max={2099}
              onChange={e => setJumpYear(parseInt(e.target.value) || currentYear)}
            />
            <label className="timewriting-jump-label">{t('common:week')}</label>
            <input
              type="number"
              className="timewriting-jump-input"
              value={jumpWeek}
              min={1}
              max={maxWeekOfYear(jumpYear)}
              onChange={e => setJumpWeek(parseInt(e.target.value) || currentWeek)}
            />
            <button className="timewriting-nav-btn timewriting-nav-btn--primary" onClick={handleJump}>
              {t('common:go')}
            </button>
          </div>

          <button className="timewriting-nav-btn" onClick={goToNextWeek}>
            {t('common:next')}&nbsp;&#8250;
          </button>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div className="text-muted py-3">{t('common:loading')}</div>
        ) : (
          <div className="timewriting-grid-wrapper">
            <table className="timewriting-grid">
              <thead>
                <tr>
                  <th className="timewriting-project-col">{t('screens:timeWriting.project')}</th>
                  {weekDates.map((d, i) => (
                    <th
                      key={i}
                      className={`timewriting-day-col${i >= 5 ? ' timewriting-day-col--weekend' : ''}`}
                    >
                      <div>{dayNames[i]}</div>
                      <div className="timewriting-day-date">{formatDayLabel(d)}</div>
                    </th>
                  ))}
                  <th className="timewriting-action-col" />
                </tr>
              </thead>

              <tbody>
                {rows.map((row, rowIdx) => (
                  <tr key={row.rowKey} className="timewriting-row">
                    <td className="timewriting-project-col">
                      {(() => {
                        const project = projects.find(p => p.id === row.projectId);
                        const isInactive = row.projectId !== '' && (!project || project.status === 'Planning' || project.status === 'Completed');
                        const hasEntries = row.days.some(cell => cell.entryId !== null);
                        if (isInactive && hasEntries) {
                          return (
                            <span className="timewriting-project-label">
                              {project ? project.name : row.projectId}
                            </span>
                          );
                        }
                        return (
                          <select
                            className="timewriting-project-select"
                            value={row.projectId}
                            onChange={e => changeRowProject(rowIdx, e.target.value)}
                          >
                            <option value="">{t('common:selectProject')}</option>
                            {activeProjects.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        );
                      })()}
                    </td>

                    {row.days.map((cell, dayIdx) => (
                      <td
                        key={dayIdx}
                        className={`timewriting-cell${dayIdx >= 5 ? ' timewriting-cell--weekend' : ''}`}
                      >
                         <input
                          type="number"
                          className="timewriting-hours-input"
                          value={cell.hours}
                          min={0}
                          step={0.5}
                          placeholder="0"
                          disabled={!row.projectId}
                          onChange={e => changeHours(rowIdx, dayIdx, e.target.value)}
                          onBlur={e => saveCell(rowIdx, dayIdx, e.target.value)}
                        />
                        <button
                          type="button"
                          className={`timewriting-note-btn${cell.description ? ' timewriting-note-btn--active' : ''}`}
                          title={cell.description ? cell.description : t('common:addNote')}
                          disabled={!row.projectId}
                          onClick={() => openNoteModal(rowIdx, dayIdx)}
                        >
                          <FaStickyNote />
                        </button>
                      </td>
                    ))}

                    <td className="timewriting-action-col">
                      <button
                        type="button"
                        className="timewriting-delete-row-btn"
                        title={t('common:removeRow')}
                        onClick={() => removeRow(rowIdx)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr>
                  <td>
                    <button
                      type="button"
                      className="timewriting-add-row-btn"
                      onClick={addRow}
                    >
                      <FaPlus />{t('common:addRow')}
                    </button>
                  </td>
                  {weekDates.map((_, i) => {
                    const total = rows.reduce((sum, row) => {
                      const h = parseFloat(row.days[i].hours);
                      return sum + (isNaN(h) ? 0 : h);
                    }, 0);
                    return (
                      <td
                        key={i}
                        className={`timewriting-total-cell${i >= 5 ? ' timewriting-cell--weekend' : ''}`}
                      >
                        {total > 0 && <span className="timewriting-total">{total}h</span>}
                      </td>
                    );
                  })}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Activity note modal */}
      <PopupDialog
        open={noteModal.open}
        title={t('common:activityNote')}
        onClose={() => setNoteModal(prev => ({ ...prev, open: false }))}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setNoteModal(prev => ({ ...prev, open: false }))}
            >
              {t('common:cancel')}
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={saveNote}>
              {t('common:save')}
            </button>
          </>
        }
      >
        <label className="form-label">
          {noteModal.open
            ? `${dayNames[noteModal.dayIndex]}, ${formatDayLabel(weekDates[noteModal.dayIndex])}`
            : ''}
        </label>
        <textarea
          className="form-control"
          rows={4}
          value={noteModal.text}
          placeholder={t('screens:timeWriting.describeActivities')}
          onChange={e => setNoteModal(prev => ({ ...prev, text: e.target.value }))}
        />
      </PopupDialog>
    </div>
  );
};

export default TimeWriting;
